import { useMemo } from 'react';
import styled from 'styled-components';
import { Carousel } from '@elfsight/lib-carousel';
import { formatFull, parse } from '@elfsight/widget-ui';
import type { MediaValue } from '../../../data/sources';
import { useSocialFeedSettings } from '../../../utils';
import { usePost } from '../../Post';
import { MediaCarouselItem } from './MediaCarouselItem';
import { MediaContainer } from './MediaContainer';
import type { PickedMediaImageProps } from './media-video';

interface MediaCarouselProps extends PickedMediaImageProps {
  items: NonNullable<MediaValue['items']>;
}

export function MediaCarousel({
  items,
  ...mediaImageProps
}: MediaCarouselProps) {
  const { language } = useSocialFeedSettings();
  const { timestamp, user } = usePost();

  const locale = language?.language || 'en';

  const carouselAriaLabel = useMemo(() => {
    const name = user?.name || user?.username || 'unknown user';
    const dateTime = timestamp ? parse(timestamp) : null;
    const date = dateTime ? formatFull(dateTime, locale) : 'unknown date';

    return `Carousel of a post by ${name}, posted on ${date}`;
  }, [user?.name, user?.username, locale, timestamp]);

  return (
    <MediaContainer>
      <CenteredCarousel
        withArrowControl
        withPaginationControl={false}
        arrowControlVariant="ghost"
        ariaLabel={carouselAriaLabel}
      >
        {items.map((item, i) => (
          <MediaCarouselItem
            key={item.src ?? i}
            item={item}
            {...mediaImageProps}
          />
        ))}
      </CenteredCarousel>
    </MediaContainer>
  );
}

const CenteredCarousel = styled(Carousel)`
  width: 100%;

  & .swiper-wrapper {
    align-items: center !important;
  }
`;
